import { flatten, uniq } from 'lodash'

export const CULTURAL_FIT_MODES = { SHORT: 'short', LONG: 'long' }

/**
 * Liest den Culture-Fit-Modus aus app_settings (career ODER partner).
 * Alles ausser 'long' ist 'short' - identisch zum Default-Parameter von
 * testBatteryConfig, deckt also auch Altdaten ohne mode ab.
 *
 * Loest den bisher duplizierten Inline-Parse ab: genau diese Duplizierung
 * plus die verstreuten 'long'-Literale waren der Mechanismus, ueber den
 * Render- und Save-Quelle im Expertenmodus auseinandergelaufen sind.
 *
 * @param {*} app_settings career.app_settings || partner.app_settings
 */
export const getCulturalFitMode = (app_settings) =>
  JSON.parse(app_settings?.cultural_fit || '{}').mode ===
  CULTURAL_FIT_MODES.LONG
    ? CULTURAL_FIT_MODES.LONG
    : CULTURAL_FIT_MODES.SHORT

/**
 * standard = true // gehoert zur Pflicht-Testbatterie, nicht abwaehlbar
 * include = true // is the best possible testbatttery
 * recommended = true // some challenges are recommended
 * optional = true // some challenges are optional inside the success screen
 *
 * Invariant fuer alles, was angezeigt ODER gespeichert wird:
 *   standard === true => include === true
 *
 * Die beiden Culture-Challenges verletzen ihn absichtlich: je Modus ist genau
 * eine von HEROS_JOURNEY / CULTURE standard-aber-nicht-included. Konsumenten
 * MUESSEN deshalb isChallengeAvailable() anwenden, bevor sie nach `standard`
 * gruppieren - sonst rendern beide Culture-Challenges als nicht abwaehlbare
 * Standardkarte, und die nicht-included verschwindet danach in der Uebersicht.
 *
 * @param {*} scores
 * @param {*} cultural_fit_mode 'short' | 'long' (siehe CULTURAL_FIT_MODES)
 * @returns configuration object for the testbattery overview
 */
export const testBatteryConfig = (
  {
    ANALOGICAL_REASONING_score,
    NUMERACY_score,
    EMOTIONS_GAME_score,
    NBACK_score,
    PROBLEM_SOLVING_score,
    DIGIT_SPAN_score,
    DELEGATION_score_responsibility
  },
  cultural_fit_mode = CULTURAL_FIT_MODES.SHORT
) => ({
  BIG_FIVE: { standard: true, include: true, recommended: false },
  HEROS_JOURNEY: {
    standard: true,
    include: cultural_fit_mode !== CULTURAL_FIT_MODES.LONG,
    recommended: false
  },
  CULTURE: {
    standard: true,
    include: cultural_fit_mode === CULTURAL_FIT_MODES.LONG,
    recommended: false
  },
  FLANKER: {
    standard: true,
    include: true,
    recommended: false // explicitly false, even though it is a game of skill / is already standard
  },
  TOWER_OF_LONDON: {
    standard: true,
    include: true,
    recommended: false // explicitly false, even though it is a game of skill / is already standard
  },
  ANALOGICAL_REASONING: {
    include: ANALOGICAL_REASONING_score.optimal >= 25,
    recommended: ANALOGICAL_REASONING_score.optimal >= 25
  },
  NUMERACY: {
    include: NUMERACY_score.optimal >= 25,
    recommended: NUMERACY_score.optimal >= 25
  },
  EMOTIONS_GAME: {
    include: EMOTIONS_GAME_score.optimal >= 50,
    recommended: EMOTIONS_GAME_score.optimal >= 50
  },
  NBACK: {
    include:
      NBACK_score.optimal >= 50 ||
      (NBACK_score.optimal >= 25 && PROBLEM_SOLVING_score.optimal >= 50),
    recommended:
      NBACK_score.optimal >= 50 ||
      (NBACK_score.optimal >= 25 && PROBLEM_SOLVING_score.optimal >= 50)
  },
  DIGIT_SPAN: {
    include:
      DIGIT_SPAN_score.optimal >= 50 ||
      (DIGIT_SPAN_score.optimal >= 25 && PROBLEM_SOLVING_score.optimal >= 50),
    recommended:
      DIGIT_SPAN_score.optimal >= 50 ||
      (DIGIT_SPAN_score.optimal >= 25 && PROBLEM_SOLVING_score.optimal >= 50)
  },
  DELEGATION: {
    include: DELEGATION_score_responsibility.optimal >= 50,
    recommended: DELEGATION_score_responsibility.optimal >= 50
  },
  RIASEC_IMAGES: { include: false, optional: true }
})

/**
 * Eine standard-aber-nicht-included Challenge ist die Culture-Challenge des
 * jeweils ANDEREN Modus. Sie darf nirgends auftauchen: im Standard-Block waere
 * sie eine nicht abwaehlbare Phantomkarte, unter "Weitere Challenges" waere
 * sie schaltbar und wuerde die Exklusion in den Daten brechen.
 *
 * Bewusst NICHT `include === true`: include:false ist bei den score-
 * abhaengigen Challenges und bei RIASEC_IMAGES der Normalfall und bedeutet
 * "nicht empfohlen", nicht "nicht verfuegbar".
 *
 * config darf undefined sein - system.challenges kann exam_ids enthalten, die
 * testBatteryConfig nicht kennt. Die bleiben verfuegbar (wie bisher).
 */
export const isChallengeAvailable = (config) =>
  !(config?.standard === true && config?.include === false)

/**
 * testBatteryConfig verlangt career_analyse-Scores, die es ausserhalb einer
 * Stelle nicht gibt. Fuer die Culture-Exklusion sind sie irrelevant: Scores
 * steuern ausschliesslich include/recommended der score-abhaengigen
 * Challenges, und die haben kein standard: true - fallen bei
 * isChallengeAvailable also nie durch. Abgesichert durch den LOW_SCORES-Fall
 * in diagnostic.test.js.
 */
const NEUTRAL_SCORES = {
  ANALOGICAL_REASONING_score: { optimal: 0 },
  NUMERACY_score: { optimal: 0 },
  EMOTIONS_GAME_score: { optimal: 0 },
  NBACK_score: { optimal: 0 },
  PROBLEM_SOLVING_score: { optimal: 0 },
  DIGIT_SPAN_score: { optimal: 0 },
  DELEGATION_score_responsibility: { optimal: 0 }
}

/**
 * isChallengeAvailable fuer Kontexte ohne career_analyse - z.B. die
 * Talentpool-Einladung, die Challenges direkt auf dem Partner konfiguriert.
 * Einziger Effekt: die Culture-Challenge des jeweils anderen Modus faellt raus.
 *
 * Bewusst ueber testBatteryConfig statt ueber ein eigenes
 * mode -> exam_id-Mapping: ein zweites Mapping waere genau die Duplizierung,
 * an der Render- und Save-Quelle im Expertenmodus schon einmal auseinander-
 * gelaufen sind.
 *
 * @param {*} exam_id
 * @param {*} cultural_fit_mode 'short' | 'long' (siehe CULTURAL_FIT_MODES)
 */
export const isChallengeAvailableForMode = (exam_id, cultural_fit_mode) =>
  isChallengeAvailable(
    testBatteryConfig(NEUTRAL_SCORES, cultural_fit_mode)[exam_id]
  )

/**
 * Die beiden Culture-Challenges, abgeleitet statt aufgezaehlt: es sind genau
 * die exam_ids, deren Verfuegbarkeit ueberhaupt vom Modus abhaengt. Aus dem
 * gleichen Grund wie bei isChallengeAvailableForMode bewusst kein eigenes
 * mode -> exam_id-Mapping.
 *
 * Konsumenten brauchen das, wenn eine gespeicherte Auswahl einen Moduswechsel
 * ueberleben soll: die Culture-Challenge des alten Modus wird nicht mehr
 * angeboten, ihre Auswahl darf aber nicht stillschweigend verfallen.
 */
export const CULTURE_CHALLENGE_IDS = Object.keys(
  testBatteryConfig(NEUTRAL_SCORES, CULTURAL_FIT_MODES.SHORT)
).filter(
  (exam_id) =>
    isChallengeAvailableForMode(exam_id, CULTURAL_FIT_MODES.SHORT) !==
    isChallengeAvailableForMode(exam_id, CULTURAL_FIT_MODES.LONG)
)

/**
 * Nicht abwaehlbare Challenges. handleToggle() im Expertenmodus ignoriert sie,
 * sie muessen deshalb unabhaengig von override_defaults.selected_challenges
 * gesetzt sein.
 *
 * @param {*} configuration Rueckgabe von testBatteryConfig
 * @returns string array / [ 'BIG_FIVE', ... ]
 */
export const getMandatoryChallenges = (configuration) =>
  Object.keys(configuration).filter(
    (exam_id) =>
      configuration[exam_id].standard === true &&
      isChallengeAvailable(configuration[exam_id])
  )

export const getDifficultyOptions = () => [
  {
    value: -1,
    label: 'create_career.hardness_of_selection.options.1',
    difficulty: 'EASY'
  },
  {
    value: 0,
    label: 'create_career.hardness_of_selection.options.2',
    difficulty: 'NORMAL'
  },
  {
    value: 1,
    label: 'create_career.hardness_of_selection.options.3',
    difficulty: 'HARD'
  }
]

/**
 * function to map challenges to covered dimensions
 *
 * @param {*} challenges / [ { exam_id: 'BIG_FIVE' }, ... ]
 * @param {*} dimensions / Object.values(system.dimensions.data)
 * @returns string array / [ 'ANALOGICAL_REASONING_score', ... ]
 */
export const mapChallengesToCoveredDimensions = ({
  challenges,
  dimensions
}) => {
  const getAllDimensions = ({ exam_id }) => {
    return dimensions
      .filter((dimension) => dimension.measured_by.includes(exam_id))
      .map(({ key }) => key)
  }

  return uniq(flatten(challenges.map(getAllDimensions)))
}
